/**
 * API client for the Creators dashboard "Brand logos" tab: the logos creator
 * report pages show next to each advertiser's name. A logo is stored on the
 * Sales advertiser registry record, and nothing shows on a client page until
 * someone picks one here.
 *
 * Backend: backend/src/routes/advertiserLogosAdmin.ts, mounted at
 * /api/admin/advertiser-logos. Every route needs clientReports.manage. Linking
 * a report name to the registry goes through the media-plan routes instead
 * (mediaPlansApi: updateAdvertiser `addAliases` needs mediaPlans.manage,
 * createAdvertiser needs sales.view).
 *
 * Errors are JSON `{ error, code }` and surface as ApiError: 400 `bad_request`
 * / `unsupported_image` / `too_small`, 404 `not_found`, 502 `fetch_failed`.
 */

import { API_BASE_URL, ApiError, apiRequest, getAuthToken } from './api';
import { downloadAuthedFile } from './mediaPlansApi';
import type {
  AdvertiserLogo,
  AdvertiserLogoList,
  LogoApplyResult,
  LogoBackground,
  LogoCandidate,
  LogoFindResult,
} from '../components/Creators/types';
import type { NeedsAdvertiser } from '../components/Sales/MediaPlans/types';

const BASE = '/api/admin/advertiser-logos';

const keyPath = (advertiserKey: string) => `${BASE}/${encodeURIComponent(advertiserKey)}`;

/** Every report advertiser of the 12-month window plus every registry record, with its match and logo. */
export async function listAdvertiserLogos(): Promise<AdvertiserLogoList> {
  const body = await apiRequest(BASE);
  return {
    window: body.window,
    truncatedMonths: body.truncatedMonths ?? [],
    rows: body.rows ?? [],
  };
}

/** Free logo suggestions per name (1–20 per call; no writes). One result per item, in order. */
export async function findLogos(items: Array<{ name: string; advertiserKey?: string }>): Promise<LogoFindResult[]> {
  const body = await apiRequest(`${BASE}/find-batch`, { method: 'POST', body: JSON.stringify({ items }) });
  return body.results ?? [];
}

export type LogoPick = Pick<LogoCandidate, 'source' | 'ref'>;

/** Use one suggestion: the server fetches, normalizes and stores it. */
export async function setAdvertiserLogo(advertiserKey: string, pick: LogoPick): Promise<AdvertiserLogo> {
  const body = await apiRequest(keyPath(advertiserKey), {
    method: 'PUT',
    body: JSON.stringify({ source: pick.source, ref: pick.ref }),
  });
  return body.logo;
}

/** Use several suggestions (1–10 per call); the server applies them one by one and reports each. */
export async function applyLogoBatch(items: Array<LogoPick & { advertiserKey: string }>): Promise<LogoApplyResult[]> {
  const body = await apiRequest(`${BASE}/apply-batch`, {
    method: 'POST',
    body: JSON.stringify({ items: items.map(({ advertiserKey, source, ref }) => ({ advertiserKey, source, ref })) }),
  });
  return body.results ?? [];
}

/**
 * Upload a PNG or JPG (at most 5 MB) as the logo. Raw fetch so the browser
 * sets the multipart boundary; failures carry the status and code like
 * apiRequest's ApiError.
 */
export async function uploadAdvertiserLogo(advertiserKey: string, file: File): Promise<AdvertiserLogo> {
  const token = await getAuthToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE_URL}${keyPath(advertiserKey)}/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` }, // no Content-Type — the browser sets the boundary
    body: form,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body?.error || `Logo upload failed (${res.status})`) as ApiError;
    err.status = res.status;
    if (body && typeof body === 'object') {
      err.body = body;
      if (typeof body.code === 'string') err.code = body.code;
    }
    throw err;
  }
  return body.logo;
}

/** Switch the plate the logo sits on. */
export async function setLogoBackground(advertiserKey: string, background: LogoBackground): Promise<AdvertiserLogo> {
  const body = await apiRequest(keyPath(advertiserKey), { method: 'PATCH', body: JSON.stringify({ background }) });
  return body.logo;
}

/** Take the logo off the record (and off every report page within about a minute). */
export async function removeAdvertiserLogo(advertiserKey: string): Promise<void> {
  await apiRequest(keyPath(advertiserKey), { method: 'DELETE' });
}

/** The "Add to registry" draft: a category suggestion, GAM candidates and a proposed key. No writes. */
export async function getRegistryDraft(name: string, gamAdvertiserId?: string): Promise<NeedsAdvertiser> {
  const body = await apiRequest(`${BASE}/registry-draft`, {
    method: 'POST',
    body: JSON.stringify(gamAdvertiserId ? { name, gamAdvertiserId } : { name }),
  });
  return body.needs;
}

/** The logo dictionary as a CSV download. */
export function downloadAdvertiserLogoCsv(fileName: string): Promise<void> {
  return downloadAuthedFile(`${BASE}/export.csv`, fileName);
}
